import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Camera, CircleStop, ScanLine } from 'lucide-react';
import {
  Code128DiagnosticResult,
  Code128DiagnosticSession,
  startDynamsoftCode128Diagnostic,
} from '../services/dynamsoftCode128Diagnostic';

type DiagnosticTone = 'error' | 'idle' | 'loading' | 'ready' | 'success';

function getErrorMessage(error: unknown) {
  if (error instanceof DOMException && error.name === 'NotAllowedError') {
    return '摄像头权限被拒绝，请在手机浏览器设置中允许访问摄像头';
  }
  const message = error instanceof Error ? error.message : String(error);
  if (/require https to use online key/i.test(message)) {
    return '第三方测试许可证要求使用 HTTPS；当前局域网 HTTP 地址无法启动识别引擎';
  }
  return message;
}

function Code128Diagnostic() {
  const cameraContainerRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<Code128DiagnosticSession | null>(null);
  const mountedRef = useRef(true);
  const startRequestRef = useRef(0);
  const [attempts, setAttempts] = useState(0);
  const [camera, setCamera] = useState('尚未启动');
  const [focus, setFocus] = useState('尚未检测');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [resolution, setResolution] = useState('尚未检测');
  const [result, setResult] = useState<Code128DiagnosticResult | null>(null);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState('点击“开始实物测试”，然后将 PMIG 条形码完整放入画面');
  const [tone, setTone] = useState<DiagnosticTone>('idle');

  const stop = useCallback(async () => {
    startRequestRef.current += 1;
    const activeSession = sessionRef.current;
    sessionRef.current = null;
    if (activeSession) {
      await activeSession.stop();
    }
    if (mountedRef.current) {
      setRunning(false);
      setStatus('测试已停止');
      setTone('idle');
    }
  }, []);

  const start = useCallback(async () => {
    if (!cameraContainerRef.current || running) {
      return;
    }
    setAttempts(0);
    setResult(null);
    setRunning(true);
    setStatus('正在初始化识别引擎…');
    setTone('loading');
    const startRequest = ++startRequestRef.current;
    try {
      const session = await startDynamsoftCode128Diagnostic(cameraContainerRef.current, {
        onAttempt: (nextAttempts) => {
          if (!mountedRef.current) return;
          setAttempts(nextAttempts);
          if (nextAttempts === 1 || nextAttempts % 10 === 0) {
            setStatus(`已分析 ${nextAttempts} 帧，尚未读到 Code128 明文`);
          }
        },
        onError: (message) => {
          if (!mountedRef.current) return;
          setStatus(`识别引擎异常：${message}`);
          setTone('error');
        },
        onLoading: (message, progress = 0) => {
          if (!mountedRef.current) return;
          setLoadingProgress(progress);
          setStatus(message);
          setTone('loading');
        },
        onReady: (details) => {
          if (!mountedRef.current) return;
          setCamera(details.camera);
          setFocus(details.focus);
          setResolution(details.resolution);
          setStatus('摄像头和 Code128 引擎已就绪，请保持条码横向并完整入框');
          setTone('ready');
        },
        onResult: (nextResult) => {
          if (!mountedRef.current) return;
          setResult(nextResult);
          setStatus(`已读取明文：${nextResult.text}`);
          setTone('success');
          navigator.vibrate?.(120);
        },
      });
      if (!mountedRef.current || startRequest !== startRequestRef.current) {
        await session.stop();
        return;
      }
      sessionRef.current = session;
    } catch (error) {
      if (!mountedRef.current || startRequest !== startRequestRef.current) return;
      setRunning(false);
      setStatus(getErrorMessage(error));
      setTone('error');
    }
  }, [running]);

  useEffect(() => {
    // React.StrictMode runs an extra setup/cleanup cycle in development.
    // Reset the flag during every setup so live SDK callbacks are not discarded.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      startRequestRef.current += 1;
      void sessionRef.current?.stop();
      sessionRef.current = null;
    };
  }, []);

  return (
    <main className="code128-diagnostic">
      <header className="code128-diagnostic__header">
        <button aria-label="返回上一页" onClick={() => window.history.back()} type="button">
          <ArrowLeft aria-hidden="true" size={22} />
        </button>
        <div>
          <strong>Code128 实物验证</strong>
          <span>独立测试页 · 不提交任何业务数据</span>
        </div>
      </header>

      <section className="code128-diagnostic__camera" aria-label="Code128 摄像头画面">
        <div ref={cameraContainerRef} className="code128-diagnostic__camera-host">
          {!running ? (
            <div className="code128-diagnostic__placeholder">
              <ScanLine aria-hidden="true" size={42} />
              <span>等待启动摄像头</span>
            </div>
          ) : null}
        </div>
        <div className="code128-diagnostic__guide" aria-hidden="true">
          <i />
          <span>请保留条码左右两侧白边</span>
        </div>
      </section>

      <section aria-live="polite" className={`code128-diagnostic__status code128-diagnostic__status--${tone}`} role="status">
        <span>{status}</span>
        {tone === 'loading' ? <progress max="100" value={loadingProgress} /> : null}
      </section>

      <section className="code128-diagnostic__details" aria-label="识别诊断信息">
        <div><span>已分析帧数</span><strong>{attempts}</strong></div>
        <div><span>视频分辨率</span><strong>{resolution}</strong></div>
        <div><span>对焦模式</span><strong>{focus}</strong></div>
        <div><span>摄像头</span><strong>{camera}</strong></div>
      </section>

      <section className={`code128-diagnostic__result${result ? ' code128-diagnostic__result--success' : ''}`}>
        <span>条码原始明文</span>
        <strong data-testid="code128-raw-result">{result?.text || '尚未读取'}</strong>
        {result ? (
          <small>
            {result.format} · 置信度 {result.confidence} · 模块宽度 {result.moduleSize.toFixed(2)}
          </small>
        ) : null}
      </section>

      <div className="code128-diagnostic__actions">
        {running ? (
          <button className="code128-diagnostic__stop" onClick={() => void stop()} type="button">
            <CircleStop aria-hidden="true" size={20} />
            停止测试
          </button>
        ) : (
          <button className="code128-diagnostic__start" onClick={() => void start()} type="button">
            <Camera aria-hidden="true" size={20} />
            开始实物测试
          </button>
        )}
      </div>

      <p className="code128-diagnostic__notice">
        当前使用第三方 SDK 的短期测试许可，仅用于确认手机浏览器能否识别真实 PMIG Code128。
      </p>
    </main>
  );
}

export default Code128Diagnostic;
